import React, { useState } from 'react';
import { Send, MapPin, Phone, Mail, MessageCircle, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';

const API_URL = '';

export const Contact = () => {
    const { t } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [acceptData, setAcceptData] = useState(false);
    const [acceptCGV, setAcceptCGV] = useState(false);
    const [form, setForm] = useState({
        name: '', company: '', phone: '', email: '', subject: '', message: ''
    });

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.subject || !form.message) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        if (!acceptData || !acceptCGV) {
            toast.error('Veuillez accepter les conditions pour envoyer le message');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error();
            toast.success(t.contactSuccess);
            setForm({ name: '', company: '', phone: '', email: '', subject: '', message: '' });
            setAcceptData(false);
            setAcceptCGV(false);
        } catch {
            toast.error(t.contactError);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAF5] flex flex-col" data-testid="contact-page">
            <Header />
            <main className="flex-1">
                {/* Header band */}
                <section className="bg-jungle text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="contact-title">
                            {t.contactTitle}
                        </h1>
                        <p className="mt-4 text-white/70 text-base max-w-xl mx-auto">
                            {t.contactDesc}
                        </p>
                    </div>
                </section>

                <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                        {/* Form */}
                        <div className="lg:col-span-3">
                            <Card className="shadow-lg border-0">
                                <CardContent className="p-6 sm:p-8">
                                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="name">{t.contactNom}</Label>
                                                <Input id="name" value={form.name} onChange={handleChange('name')}
                                                    placeholder={t.contactNomPlaceholder} className="mt-1" required
                                                    data-testid="contact-name-input" />
                                            </div>
                                            <div>
                                                <Label htmlFor="company">{t.contactSociete}</Label>
                                                <Input id="company" value={form.company} onChange={handleChange('company')}
                                                    placeholder={t.contactSocietePlaceholder} className="mt-1"
                                                    data-testid="contact-company-input" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <Label htmlFor="phone">{t.contactTelephone}</Label>
                                                <Input id="phone" type="tel" value={form.phone} onChange={handleChange('phone')}
                                                    placeholder={t.contactTelPlaceholder} className="mt-1"
                                                    data-testid="contact-phone-input" />
                                            </div>
                                            <div>
                                                <Label htmlFor="email">{t.contactEmail}</Label>
                                                <Input id="email" type="email" value={form.email} onChange={handleChange('email')}
                                                    placeholder={t.contactEmailPlaceholder} className="mt-1" required
                                                    data-testid="contact-email-input" />
                                            </div>
                                        </div>
                                        <div>
                                            <Label htmlFor="subject">{t.contactSujet}</Label>
                                            <Input id="subject" value={form.subject} onChange={handleChange('subject')}
                                                placeholder={t.contactSujetPlaceholder} className="mt-1" required
                                                data-testid="contact-subject-input" />
                                        </div>
                                        <div>
                                            <Label htmlFor="message">{t.contactMessage}</Label>
                                            <textarea id="message" value={form.message} onChange={handleChange('message')}
                                                placeholder={t.contactMessagePlaceholder} required rows={5}
                                                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                                                data-testid="contact-message-input" />
                                        </div>

                                        {/* RGPD Checkboxes */}
                                        <div className="space-y-3 pt-1">
                                            <label className="flex items-start gap-3 cursor-pointer group" data-testid="rgpd-accept-data">
                                                <input type="checkbox" checked={acceptData} onChange={(e) => setAcceptData(e.target.checked)}
                                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-jungle focus:ring-jungle flex-shrink-0" />
                                                <span className="text-xs text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
                                                    J'accepte que les informations saisies soient utilisees pour apporter une reponse par mail ou par telephone a ma demande de contact aupres de l'entreprise.
                                                </span>
                                            </label>
                                            <label className="flex items-start gap-3 cursor-pointer group" data-testid="rgpd-accept-cgv">
                                                <input type="checkbox" checked={acceptCGV} onChange={(e) => setAcceptCGV(e.target.checked)}
                                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-jungle focus:ring-jungle flex-shrink-0" />
                                                <span className="text-xs text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
                                                    J'ai pris connaissance des{' '}
                                                    <a href="/#/cgv" target="_blank" rel="noopener noreferrer"
                                                        className="text-red-600 underline hover:text-red-800 font-medium"
                                                        onClick={(e) => e.stopPropagation()}>
                                                        conditions generales d'utilisation du site
                                                    </a>.
                                                </span>
                                            </label>
                                        </div>

                                        <Button type="submit" disabled={loading || !acceptData || !acceptCGV}
                                            className="w-full bg-jungle hover:bg-jungle/90 h-12 text-base font-semibold"
                                            data-testid="contact-submit-btn">
                                            {loading ? (
                                                <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                            ) : (
                                                <>
                                                    <Send className="w-4 h-4 mr-2" />
                                                    {t.contactEnvoyer}
                                                </>
                                            )}
                                        </Button>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Sidebar info */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card className="border-0 shadow-lg bg-jungle text-white">
                                <CardContent className="p-6">
                                    <h3 className="font-heading font-semibold text-lg mb-5">{t.contactNosCoordonnees}</h3>
                                    <div className="space-y-5">
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <MapPin className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div className="text-sm text-white/80">
                                                <p className="font-medium text-white">CENTRA 307</p>
                                                <p>94586 RUNGIS CEDEX</p>
                                                <p className="mt-1">1 RUE DE LA CORDERIE</p>
                                                <p>94550 CHEVILLY-LARUE</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <Phone className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div className="text-sm">
                                                <p className="text-white font-medium">Aisaoui Saber</p>
                                                <a href="tel:0662667639" className="text-white/70 hover:text-leaf transition-colors">06 62 66 76 39</a>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-leaf/20 flex items-center justify-center flex-shrink-0">
                                                <Mail className="w-4 h-4 text-leaf" />
                                            </div>
                                            <div className="text-sm">
                                                <a href="mailto:asaber@prixdeal.fr" className="text-white/70 hover:text-leaf transition-colors">asaber@prixdeal.fr</a>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                                                <MessageCircle className="w-4 h-4 text-green-400" />
                                            </div>
                                            <div className="text-sm">
                                                <p className="text-white font-medium">WhatsApp</p>
                                                <a href="https://wa.me/33764163496?text=Bonjour%20PRIXDEAL" target="_blank" rel="noopener noreferrer"
                                                    className="text-green-400 hover:text-green-300 transition-colors">07 64 16 34 96</a>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card className="border-0 shadow-lg">
                                <CardContent className="p-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <Building2 className="w-5 h-5 text-jungle" />
                                        <h3 className="font-heading font-semibold text-base text-jungle">PRIXDEAL SAS</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground leading-relaxed">
                                        Grossiste en fruits, legumes, plantes et produits africains.
                                        Situe au Marche International de Rungis.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default Contact;
